// ─── Task Management Types ───
import type { Client } from './client';
import type { DataverseTaskLink } from './dataverse';

/**
 * Priority level for a task. Determines urgency and display order.
 * @example Priority.HIGH
 */
export const Priority = {
  /** Immediate action required */
  URGENT: 'URGENT',
  /** High priority, should be completed soon */
  HIGH: 'HIGH',
  /** Standard priority */
  NORMAL: 'NORMAL',
  /** Low priority, can be deferred */
  LOW: 'LOW',
  /** No specific priority */
  NONE: 'NONE',
} as const;

/** Priority type — one of URGENT, HIGH, NORMAL, LOW, NONE */
export type Priority = (typeof Priority)[keyof typeof Priority];

/**
 * Task dependency relationship type (used in Gantt view).
 * Defines how dependent tasks relate to their predecessors.
 */
export const DependencyType = {
  /** Dependent task must start after predecessor finishes */
  FINISH_TO_START: 'FINISH_TO_START',
  /** Dependent task must start when predecessor starts */
  START_TO_START: 'START_TO_START',
  /** Dependent task must finish when predecessor finishes */
  FINISH_TO_FINISH: 'FINISH_TO_FINISH',
  /** Dependent task must finish when predecessor starts */
  START_TO_FINISH: 'START_TO_FINISH',
} as const;

/** Dependency type — one of FINISH_TO_START, START_TO_START, FINISH_TO_FINISH, START_TO_FINISH */
export type DependencyType = (typeof DependencyType)[keyof typeof DependencyType];

/**
 * Semantic category for a per-list {@link Status} (ADR 0014). Buckets an arbitrary custom
 * status into one of six fixed, cross-list taxonomy values so reporting rollups read the same
 * way across every space. Aligned 1:1 with the design tokens in `tailwind.config.ts`
 * (`status.todo` / `.in-progress` / `.review` / `.blocked` / `.done` / `.cancelled`).
 * Stored as a `String` column (house pattern, like {@link Priority}); the authoritative guard
 * is the shared `StatusCategoryEnum` Zod schema.
 * @example StatusCategory.DONE
 */
export const StatusCategory = {
  /** Not started / backlog (the default bucket) */
  TODO: 'TODO',
  /** Actively being worked */
  IN_PROGRESS: 'IN_PROGRESS',
  /** Awaiting review / approval */
  REVIEW: 'REVIEW',
  /** Stalled on an external blocker */
  BLOCKED: 'BLOCKED',
  /** Finished (counts toward completion; sets `Task.completedAt`) */
  DONE: 'DONE',
  /** Abandoned / won't do */
  CANCELLED: 'CANCELLED',
} as const;

/** Status category — one of TODO, IN_PROGRESS, REVIEW, BLOCKED, DONE, CANCELLED */
export type StatusCategory = (typeof StatusCategory)[keyof typeof StatusCategory];

/**
 * A status represents a Kanban column (e.g., "To Do", "In Progress", "Done").
 * Each status belongs to exactly one list and can contain multiple tasks.
 */
export interface Status {
  /** Unique identifier (UUID v4) */
  id: string;
  /** Status name (e.g., "To Do") */
  name: string;
  /** Hex color code for visual identification (e.g., "#3B82F6") */
  color: string;
  /** Position in the list (for ordering columns) */
  position: number;
  /** Semantic reporting category — one of the six fixed buckets (ADR 0014). Defaults to TODO. */
  category: StatusCategory;
  /** ID of the list this status belongs to */
  listId: string;
}

/**
 * A task in the project management system.
 * Tasks can be organized into lists, assigned to users, and have various attributes
 * like priority, due dates, and estimates.
 */
export interface Task {
  /** Unique identifier (UUID v4) */
  id: string;
  /** Task title (required, 1-500 characters) */
  title: string;
  /**
   * The Microsoft Dataverse row this task was imported from (#415), or `null` when the task
   * is standalone. `!= null` IS the linked/standalone badge predicate.
   *
   * Optional for backwards compatibility with pre-#415 payloads, matching `actualMinutes` and
   * `completedAt` above; absent means unknown rather than standalone.
   */
  dataverseLink?: DataverseTaskLink | null;
  /** Optional task description or notes */
  description: string | null;
  /** Priority level (urgency) */
  priority: Priority;
  /** Position within the list (for ordering, drag-and-drop) */
  position: number;
  /** When the task is due (ISO 8601 datetime) */
  dueDate: string | null;
  /** When the task should start (ISO 8601 datetime) */
  startDate: string | null;
  /** Time estimate in minutes */
  estimate: number | null;
  /**
   * Real time worked in minutes (ADR 0018 slice 2) — one adjustable scalar, the
   * single source of truth for actuals (TimeEntry stays dormant). Optional for
   * backwards compatibility with pre-slice-2 payloads; absent means unknown.
   */
  actualMinutes?: number | null;
  /**
   * When the task's status category last transitioned into DONE (ISO 8601), null if
   * not done. Maintained server-side at the status chokepoints since ADR 0014 but only
   * carried on the wire since ADR 0018 slice 2 — optional for backwards compatibility.
   */
  completedAt?: string | null;
  /**
   * When someone CONFIRMED this task missed its deadline (ADR 0018 slice 4) — the
   * stored, acknowledged event that fired the out-of-deadline notifications; null =
   * not confirmed (a computed-overdue task may exist without this being set). Set
   * only via POST /tasks/{id}/confirm-deadline-missed, never raw-PATCHable; cleared
   * server-side when a manager reschedules the task to a future due date.
   */
  deadlineMissedAt?: string | null;
  /**
   * Marks this task as a milestone (Phase 12 Batch 1). Milestones are a distinct
   * analytics lens (the `milestoneRate` KPI). Optional for backwards compatibility
   * with pre-Batch-1 payloads; absent means not a milestone.
   */
  isMilestone?: boolean;
  /** Version number for optimistic locking (incremented on updates) */
  version: number;
  /** ID of the list this task belongs to */
  listId: string;
  /** ID of the current status (Kanban column) */
  statusId: string;
  /** ID of parent task (for subtasks) */
  parentId: string | null;
  /** ID of the client this task is done for (null if not tagged; optional for backwards compatibility) */
  clientId?: string | null;
  /**
   * Phase of a client project this task is tagged to (ADR 0039 D2, #1200), or null. Only the
   * id travels: the project name is resolved by the viewer, who may not see a TEAM project.
   */
  projectPhaseId?: string | null;
  /**
   * ID of this task's type (ADR 0032, #398), or null when untyped. Optional for
   * backwards compatibility with pre-ADR-0032 payloads.
   */
  taskTypeId?: string | null;
  /**
   * GHE #1291 — el área de conocimiento a la que pertenece este trabajo, o `null`.
   *
   * Se hereda de `List.defaultSpecialtyId` al crear cuando quien crea no dice nada, y desde ese
   * momento es un campo PROPIO de la tarea: cambiar el defecto de la lista no la toca.
   *
   * Opcional por compatibilidad con cargas anteriores a la columna.
   */
  specialtyId?: string | null;
  /**
   * Per-task override of the type's validation-plan requirement (ADR 0032 D2).
   *
   * THREE meaningful states, and collapsing any two is a bug: `null` = inherit the type,
   * `true`/`false` = a deliberate human decision. Never read this alone to answer "does
   * finishing this need a plan?" — that is the resolved two-level answer, and the server
   * reports it as `requiresValidationPlan` on the completion preview.
   */
  requiresValidation?: boolean | null;
  /**
   * #738 — link to the work item this task mirrors elsewhere (Azure DevOps on import, anything
   * `https:` by hand), or null when there is none. Optional for backwards compatibility with
   * payloads predating the column.
   *
   * 🚨 Always `https:` with a host — enforced on the way IN by `ExternalUrlSchema`, never
   * assumed at render. Renders as a link, so it must carry `rel="noopener noreferrer"`.
   */
  externalUrl?: string | null;
  /**
   * GHE #683 — cada cuánto se repite esta tarea, o null/ausente si no se repite.
   *
   * Uno de `TASK_REPEAT_RULES`. La ficha lo pinta como el selector «Repetir», y al marcar la
   * tarea como hecha el servidor engendra la siguiente ocurrencia dentro de la MISMA transacción
   * del cierre.
   */
  repeatRule?: string | null;
  /** GHE #683 — fin de la serie, inclusive. Null = indefinida. */
  repeatUntil?: string | null;
  /**
   * GHE #683 — a qué serie pertenece, o null si la tarea nunca ha engendrado ni ha nacido de
   * otra.
   *
   * 🚨 SÓLO LECTURA desde el cliente, y no hay forma de escribirlo por la API: es la clave de
   * idempotencia que impide que reabrir y volver a cerrar duplique la serie. Un cliente que
   * pudiera elegirlo podría fabricar dos ocurrencias vivas.
   */
  seriesId?: string | null;
  /**
   * ADR 0037 (#1085) — the previous development this task came out of, or null.
   *
   * 🚨 PROVENANCE ONLY. This is not a `TaskDependency` and must never be read by scheduling
   * code: relating a task changes no date, no plan and no Gantt geometry (ADR 0037 D1).
   */
  relatedTaskId?: string | null;
  /**
   * ADR 0037 (#1085) — who did the related development.
   *
   * Seeded from the related task's first assignee at link time, then owned by the human
   * (ADR 0037 D4): it records who DID the work, which is not always who the old task is
   * assigned to. Only meaningful while `relatedTaskId` is set.
   */
  previousDeveloperId?: string | null;
  /**
   * Related-task summary, included when `relatedTaskId` is set.
   *
   * 🚨 `title` is nullable and the nullability IS the feature (ADR 0037 D8). A reader without
   * access to the related task gets `accessible: false` and no title — which must render as
   * "Sin acceso", NEVER as blank. Blank reads as "there is nothing here", a different and
   * false statement. Same for a task soft-deleted after being linked (`archived: true`).
   */
  relatedTask?: RelatedTaskSummary | null;
  /** Previous developer's details, included when `previousDeveloperId` is set. */
  previousDeveloper?: {
    id: string;
    displayName: string;
    email: string;
    avatarUrl: string | null;
    /** ADR 0029: when this person was marked as having left, or null. */
    disabledAt: string | null;
  } | null;
  /**
   * Client summary (included in responses when clientId is set). `archived` is true
   * when the client has been soft-deleted (#95 cluster C): the task keeps the FK and
   * the chip still renders — read-only, styled as archived — but the client is gone
   * from every picker and cannot be newly assigned.
   */
  client?: (Pick<Client, 'id' | 'name' | 'color'> & { archived: boolean }) | null;
  /** ID of user who created this task */
  createdById: string;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last update timestamp (ISO 8601) */
  updatedAt: string;
  /** Soft delete timestamp (ISO 8601) — null if not deleted */
  deletedAt: string | null;
  /** Users assigned to this task (included in detail view) */
  assignments?: TaskAssignment[];
  /** Comments on this task (included in detail view) */
  comments?: TaskComment[];
  /** Subtasks of this task (included in detail view) */
  subtasks?: Task[];
}

/**
 * The single pull-forward candidate offered when a task finishes early
 * (ADR 0018 slice 3, blueprint §4.1): the assignee's next scheduled task in the
 * SAME space (owner decision), never pulled ahead of an unmet finish-to-start
 * dependency. At most one candidate — chaining is explicitly out of scope.
 */
export interface PullForwardCandidate {
  /** The task that would move (UUID) */
  taskId: string;
  /** Its title, for the confirm preview */
  title: string;
  /** Current scheduled start (ISO 8601) */
  currentStartDate: string;
  /** Current due date (ISO 8601), null if none */
  currentDueDate: string | null;
  /** Proposed new start — the assignee's next working day after completion (ISO 8601) */
  newStartDate: string;
  /** Proposed new due date, recomputed calendar-aware from the estimate (ISO 8601), null if no estimate */
  newDueDate: string | null;
  /** Version of the candidate task the preview was computed against (TOCTOU guard on apply) */
  version: number;
}

/**
 * Response of GET /api/v1/tasks/{id}/completion-preview — everything the
 * completion dialog needs to render in one round trip.
 */
export interface CompletionPreview {
  /** Calendar-aware suggested actual minutes (null when nothing can be suggested) */
  suggestedActualMinutes: number | null;
  /** The at-most-one pull-forward offer, or null */
  pullForwardCandidate: PullForwardCandidate | null;
  /** True when this is a client task (clientId != null) — the #25 gate applies */
  requiresValidationPlan: boolean;
  /** True when a VALIDATION_PLAN attachment is already on the task */
  hasValidationPlan: boolean;
}

/** Meta returned by POST /tasks/{id}/complete */
export interface CompleteTaskMeta {
  /** Whether the pull-forward was applied (false when skipped, absent candidate, or not requested) */
  pullForwardApplied: boolean;
}

/**
 * A user assignment to a task (many-to-many relationship).
 * Represents that a user is responsible for or working on a task.
 */
export interface TaskAssignment {
  /** Unique identifier */
  id: string;
  /** ID of the task being assigned */
  taskId: string;
  /** ID of the user being assigned */
  userId: string;
  /** Assigned user details (included when task is fetched with assignments) */
  user?: {
    id: string;
    displayName: string;
    email: string;
    avatarUrl: string | null;
    /**
     * ADR 0029: when this person was marked as having left, or null.
     *
     * Load-bearing for the Gantt, which synthesises a swimlane for anyone holding a task even
     * when they are no longer a member (`laneLayout.ts`). That merge happens CLIENT-side from
     * this payload, so no server-side roster filter can reach it — the flag has to travel with
     * the assignment or a departed colleague keeps their lane.
     */
    disabledAt: string | null;
  };
}

/**
 * A dependency relationship between two tasks (for Gantt view).
 * Indicates that one task depends on another completing first.
 */
export interface TaskDependency {
  /** Unique identifier */
  id: string;
  /** ID of the dependent task (the one that depends on others) */
  taskId: string;
  /** ID of the task this one depends on (the predecessor) */
  dependsOnId: string;
  /** Type of dependency relationship */
  type: DependencyType;
}

/**
 * A related task projected into a {@link TaskDependencySummary} row (GHE #678) — just
 * enough for the task-detail dependency panel to render a chip and a due-date badge, NOT
 * the full {@link Task} entity.
 */
export interface TaskDependencyRelatedTask {
  /** Related task's id (UUID v4) */
  id: string;
  /** Related task's title */
  title: string;
  /** Related task's current status name (its Kanban column) */
  statusName: string;
  /** Related task's due date (ISO 8601), null if unscheduled */
  dueDate: string | null;
  /**
   * Related task's start date (ISO 8601), null if unscheduled (GHE #698).
   *
   * Needed because dependency slack is measured against the dates people actually assigned, and
   * WHICH two dates an edge constrains depends on its type: FINISH_TO_START compares the
   * predecessor's `dueDate` to the successor's `startDate`, but START_TO_START compares the two
   * start dates. Exposing only `dueDate` made three of the four types incomputable.
   */
  startDate: string | null;
}

/**
 * One row of `GET /api/v1/tasks/{taskId}/dependencies` (GHE #678), in EITHER direction —
 * the `TaskDependency` row's own id (so the panel's delete action can target it) plus the
 * relationship type and the related task at the OTHER end of the edge.
 */
export interface TaskDependencySummary {
  /** TaskDependency row id */
  id: string;
  /** Relationship type */
  type: DependencyType;
  /** The related task at the other end of this edge */
  task: TaskDependencyRelatedTask;
}

/**
 * Response payload for `GET /api/v1/tasks/{taskId}/dependencies` (GHE #678) — a task's
 * dependencies in BOTH directions, for the task-detail panel: `dependsOn` is what this task
 * is waiting for, `blocks` is what is waiting for this task. Both matter — a panel showing
 * only one direction is misleading.
 *
 * A related task the caller cannot READ is silently OMITTED from either array, never
 * included with a leaked title — the same "filter to the visible subset, drop the rest,
 * no-leak" precedent `filterVisibleUserIds` (`apps/api/src/lib/assert-access.ts`) uses for
 * the equivalent cross-tenant-disclosure problem on user rosters.
 */
export interface TaskDependencyList {
  /** Tasks this task depends on (predecessors), visible-to-caller subset only */
  dependsOn: TaskDependencySummary[];
  /** Tasks that depend on this task (successors), visible-to-caller subset only */
  blocks: TaskDependencySummary[];
}

/**
 * One outgoing edge for the List view's Slack column — GHE #698 / #1023 step 5.
 *
 * 🚨 OUTGOING only, and that is the definition of the column. A row's Slack answers *"how much
 * can this task slip before it hits something?"*, which is the gap to the work waiting on it —
 * the edges where this task is the PREDECESSOR. Incoming edges say how much room the plan left
 * BEFORE it, a different question that the task-detail panel already answers in both directions.
 *
 * ⚠️ The predecessor's own dates are deliberately NOT carried here. The List already has them on
 * the row it is rendering, and using those keeps the number consistent with the dates on screen —
 * the same reason `DependenciesSection` measures against its own panel's `self`.
 */
export interface TaskSlackEdge {
  /** Relationship type — decides WHICH pair of dates the edge constrains. */
  type: DependencyType;
  /** The dependent task, so the cell can name what is waiting. */
  successorId: string;
  successorTitle: string;
  /** The successor's dates. Either may be null, which makes the edge's slack "unknown". */
  successorStartDate: string | null;
  successorDueDate: string | null;
}

/**
 * Response payload for `GET /api/v1/lists/{listId}/dependency-slack` — every outgoing edge for
 * the tasks in one list, in one request.
 *
 * 🚨 Exists because the per-task endpoint would be one request per ROW. A list of 200 tasks
 * cannot ask 200 questions to fill a column.
 *
 * ⚠️ A task with NO outgoing edge is ABSENT from `rows` rather than present with an empty array,
 * and the UI must render that as "no dependencies" — never as zero slack. Zero means the link is
 * tight; absent means there is no link at all, and #698 says plainly which of the two mistakes is
 * dangerous: *"an empty overview reads as 'no critical tasks' rather than 'no data'"*.
 *
 * Successors the caller cannot READ are omitted, exactly as `TaskDependencyList` omits them.
 */
export interface ListDependencySlack {
  rows: { taskId: string; edges: TaskSlackEdge[] }[];
}

/**
 * A comment on a task (part of collaboration features).
 */
export interface TaskComment {
  /** Unique identifier (UUID v4) */
  id: string;
  /** Comment text (markdown supported) */
  content: string;
  /** ID of the task this comment is on */
  taskId: string;
  /** ID of the user who wrote this comment */
  userId: string;
  /** When the comment was created (ISO 8601) */
  createdAt: string;
  /** When the comment was last edited (ISO 8601) */
  updatedAt: string;
  /** Soft delete timestamp (ISO 8601) — null if not deleted */
  deletedAt: string | null;
}

/**
 * A time entry tracking actual work spent on a task.
 */
export interface TimeEntry {
  /** Unique identifier (UUID v4) */
  id: string;
  /** ID of the task this entry is for */
  taskId: string;
  /** ID of the user who worked on this task */
  userId: string;
  /** Duration of work in minutes */
  duration: number;
  /** Optional note about the work (e.g., "Fixed login bug") */
  note: string | null;
  /** When the work period started (ISO 8601) */
  startedAt: string;
  /** When the work period ended (ISO 8601) */
  endedAt: string | null;
  /** When this entry was recorded (ISO 8601) */
  createdAt: string;
}

/**
 * What the UI needs to render a related development (ADR 0037 D8, #1085) without a second
 * request, and without ever rendering blank.
 *
 * The two flags are NOT redundant with `title === null`. They say WHY it is missing, and the
 * two reasons need different words on screen: an archived task still exists and the link is
 * still true; an inaccessible one may be perfectly alive and simply none of this reader's
 * business. Collapsing them would make a permission boundary look like a deletion.
 */
/** One person on the related task — the avatar triple, never a full user (ADR 0037 D8). */
export interface RelatedTaskAssignee {
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface RelatedTaskSummary {
  /** Always present — the link is real even when its target cannot be shown */
  id: string;
  /** null when the reader lacks access; render "Sin acceso", never blank */
  title: string | null;
  /** Owning list's name, or null when inaccessible */
  listName: string | null;
  /** Status name at read time, or null when inaccessible */
  statusName: string | null;
  /**
   * Who currently holds the related task, or an EMPTY ARRAY when inaccessible.
   *
   * ⚠️ Distinct from `previousDeveloper` on the parent task, and the two may legitimately
   * disagree: this is a LIVE fact about the related task that moves when someone reassigns it,
   * while `previousDeveloper` is a snapshot frozen at link time. Both are correct; they answer
   * different questions.
   *
   * 🚨 Empty when `accessible` is false — it rides the SAME gate as `title`. A face is as much
   * a disclosure as a title, so it must vanish for the same reader and by the same rule (D8).
   */
  assignees: RelatedTaskAssignee[];
  /** True when the related task was soft-deleted AFTER being linked — link kept (ADR 0037 D8) */
  archived: boolean;
  /** False when the reader may not see the related task */
  accessible: boolean;
}
